<script>
import { mapActions } from 'vuex'

export default {
  name: 'TracksList',
  components: {
    Action: () => import('@/components/general/Action'),
    Dropdown: () => import('@/components/general/Dropdown'),
    Datatable: () => import('@/components/general/Datatable'),
    FilterList: () => import('@/components/general/FilterList'),
    Pagination: () => import('@/components/general/Pagination'),
    DropdownLink: () => import('@/components/general/DropdownLink'),
    ContentHeader: () => import('@/components/general/ContentHeader'),
    FilterListSearch: () => import('@/components/general/FilterListSearch'),
    Tabs: () => import('@/components/general/Tabs'),
    SelectField: () => import('@/components/general/SelectField'),
    PageLimitSelector: () => import('@/components/general/PageLimitSelector')
  },

  data () {
    return {
      pagination: {
        page: 1,
        limit: 10,
        lastPage: null,
        filter: {
          only_name: true
        }
      },

      search: null,
      modalToggleActiveOpen: false,
      trackId: null,
      genreFilter: null,
      genreFilterList: [
        {
          text: this.$t('tracks.list:filter.1'),
          value: 'curso_ead'
        },
        {
          text: this.$t('tracks.list:filter.2'),
          value: 'dicas_whatsapp'
        },
        {
          text: this.$t('tracks.list:filter.3'),
          value: 'presencial'
        }
      ],

      tabLinks: [
        {
          location: { name: 'tracks.index' },
          text: this.$t('global:menu.tracks')
        },
        {
          location: { name: 'tracks.configs' },
          text: this.$t('solutions.tabs:link.2')
        }
      ]
    }
  },

  computed: {
    tracksList () {
      return this.$store.getters.tracksList
    },

    canWrite () {
      return this.getContextPermission('tracks') === 'write'
    },

    isNonProdEnv () {
      return process.env.NODE_ENV !== 'production'
    }
  },

  watch: {
    'pagination.page' () {
      this.getTrackList()
    }
  },

  created () {
    this.getTrackList()
  },

  methods: {
    ...mapActions([
      'setFetching',
      'setFeedback',
      'attemptGetTracksList',
      'attemptGetTracksSearch',
      'attemptManageTrack',
      'attemptToggleActiveTrack',
      'attemptUpdateTrack'
    ]),

    getToggleClass (isEnabled) {
      return isEnabled ? 'dropdown-toggle--on' : 'dropdown-toggle--off'
    },

    prevPage () {
      if (this.pagination.page > 1) {
        this.pagination.page -= 1
      }
    },

    nextPage () {
      this.pagination.page += 1
    },

    firstPage () {
      this.pagination.page = 1
    },

    lastPage () {
      this.pagination.page = this.pagination.lastPage
    },

    getTrackList () {
      this.setFetching(true)

      this.pagination.order = {
        active: 'desc',
        name: 'asc'
      }

      if (this.search !== '' && this.search !== null) {
        this.attemptGetTracksSearch(this.pagination).then(pagination => {
          this.pagination.lastPage = pagination.lastPage
        }).finally(() => {
          this.setFetching(false)
        })

        return
      }

      this.attemptGetTracksList(this.pagination).then(pagination => {
        this.pagination.lastPage = pagination.lastPage
      }).finally(() => {
        this.setFetching(false)
      })
    },

    onLimitChange (newLimit) {
      this.pagination.limit = newLimit

      if (this.pagination.page === 1) {
        this.getTrackList()
      } else {
        this.pagination.page = 1
      }
    },

    getTrackLength (trackId) {
      const track = this.tracksList.find(track => track.id === trackId)

      return track.learningPathSolutions.length
    },

    createNewTrack () {
      this.attemptManageTrack(null)
      this.$router.push({ name: 'tracks.manage' })
    },

    searchTrack (search) {
      this.search = search

      this.pagination.query = {
        name: search
      }

      this.firstPage()
      this.getTrackList()
    },

    edit (trackId, tab = 1) {
      this.$router.push({
        name: 'tracks.manage',
        params: {
          id: trackId,
          tab: tab
        }
      })
    },

    openPage (track) {
      if (track.partner && track.partner.id) {
        window.open(`${process.env.VUE_APP_PORTAL_URL}conecta-saber/trilha-de-aprendizagem/${track.slug}`, '_blank')

        return
      }

      window.open(`${process.env.VUE_APP_PORTAL_URL}trilha-de-aprendizagem/${track.slug}`, '_blank')
    },

    toggleActive (trackId, toggleFlag) {
      this.setFetching(true)

      this.attemptToggleActiveTrack(trackId).then(() => {
        this.getTrackList()
        const toggleValue = toggleFlag ? 'deactivate' : 'activate'

        this.setFeedback({ message: this.$t(`tracks:feedback.toggle.${toggleValue}.success`) })
      }).catch((e) => {
        if (e.data.error.message === 'learning_path_solution_not_found') {
          this.setFeedback({ message: this.$t('global:form.tracks.toggle.learning_path.solution.error') })
        } else if (e.data.error.message === 'learning_path_workload_empty') {
          this.setFeedback({ message: this.$t('global:form.tracks.toggle.learning_path.workload.error') })
        } else {
          this.setFeedback({ message: this.$t('global.error') })
        }
      }).finally(() => {
        this.setFetching(false)
      })
    },

    buildTrackUpdateData (apiData) {
      const themes = []

      if (apiData.learningPathThemes) {
        apiData.learningPathThemes.forEach(lpt => themes.push(lpt.theme.alias))
      }

      const entrepreneurProfiles = []

      if (apiData.learningPathEntrepreneurProfiles) {
        apiData.learningPathEntrepreneurProfiles.forEach(lpep => entrepreneurProfiles.push(lpep.entrepreneurProfile.id))
      }

      const solutions = []

      if (apiData.learningPathSolutions) {
        apiData.learningPathSolutions.filter(s => s.active === 1).forEach(lps => {
          solutions.push(lps.solution?.id || lps.id)
        })
      }

      const thematicPages = []

      if (apiData.thematicPageLearningPaths) {
        apiData.thematicPageLearningPaths.forEach(tplp => thematicPages.push(tplp.thematicPage?.id || tplp.id))
      }

      return {
        id: apiData.id,
        name: apiData.name,
        slug: apiData.slug,
        description: apiData.description,
        status: apiData.status,
        active: apiData.active,
        workload: apiData.workload || '0h',
        automaticWorkload: apiData.automaticWorkload ? 1 : 0,
        call_text: apiData.callText,
        preview_video: apiData.previewVideo,
        audience: apiData.audience,
        differentials: apiData.differentials,
        show_on_portal: apiData.showOnPortal ? 1 : 0,
        exclusiveToCompany: apiData.exclusiveToCompany ? 1 : 0,
        seoTitle: apiData.seoTitle || apiData.name,
        seoDescription: apiData.seoDescription || apiData.description,
        seoKeywords: apiData.seoKeywords || '',
        certificate_id: apiData.certificate?.id || null,
        themes: themes,
        entrepreneur_profiles: entrepreneurProfiles,
        solutions: solutions,
        thematicPages: thematicPages,
        tags: apiData.tags || [],
        registrationStatus: apiData.registrationStatus
      }
    },

    toggleField (trackId, fieldName, currentValue) {
      this.setFetching(true)

      this.attemptManageTrack(trackId)
        .then(({ data }) => {
          const updateData = this.buildTrackUpdateData(data)
          const newValue = currentValue ? 0 : 1

          updateData[fieldName] = newValue

          return this.attemptUpdateTrack({
            id: trackId,
            data: updateData,
            isLinkedTab: false
          })
        })
        .then(() => {
          this.getTrackList()
          const status = currentValue ? 'desativado' : 'ativado'

          this.setFeedback({ message: `Campo ${status} com sucesso` })
        })
        .catch(() => {
          this.setFeedback({ message: this.$t('global.error') })
        })
        .finally(() => {
          this.setFetching(false)
        })
    }
  }
}
</script>

<template>
  <div class="main-content">
    <ContentHeader
      :title="$t('tracks:header.title')"
      :description="$t('tracks:header.description')"
      background="/assets/images/themes/default/png/tracks_banner.png"
    >
      <Tabs
        v-if="canWrite"
        slot="tabs"
        dark
        :links="tabLinks"
      />
    </ContentHeader>
    <FilterList>
      <Action
        v-if="canWrite"
        slot="button"
        primary
        large
        fixed-width
        type="button"
        :text="$t('tracks.list:btn.add')"
        @click="createNewTrack"
      />
      <SelectField
        v-model="genreFilter"
        :label="$t('tracks.list:filter.1')"
        :items="genreFilterList"
      />
      <PageLimitSelector
        slot="order"
        :value="pagination.limit"
        storage-key="page_limit_tracks"
        @input="onLimitChange"
      />
      <FilterListSearch
        slot="search"
        :hide-error-details="true"
        @search="searchTrack"
      />
    </FilterList>
    <div
      v-if="tracksList.length > 0"
      class="center tracks-table"
    >
      <Datatable :items="tracksList">
        <template slot="thead">
          <tr>
            <th class="th">
              {{ $t('tracks.list:datatable.header.1') }}
            </th>
            <th class="th">
              {{ $t('tracks.list:datatable.header.2') }}
            </th>
            <th class="th text-center">
              {{ $t('solutions.list:datatable.header.6') }}
            </th>

            <th class="th">
              {{ $t('tracks.list:datatable.header.3') }}
            </th>
            <th
              class="th"
              width="130"
            >
              {{ $t('global:datatable.audit.created-at') }}
            </th>
            <th
              class="th"
              width="180"
            >
              {{ $t('global:datatable.audit.updated-at') }}
            </th>
            <th
              class="th"
              width="40"
            />
          </tr>
        </template>
        <template
          slot="tbody"
          slot-scope="props"
        >
          <tr>
            <td class="td">
              <span class="td-text bolder">{{ props.item.name }}</span>
            </td>
            <td class="td">
              <span class="td-text">{{ getTrackLength(props.item.id) }}</span>
            </td>

            <td class="td text-center">
              <span
                :class="{ 'td-tag active-ribbon': Boolean(props.item.showOnPortal), 'td-tag inactive-ribbon': Boolean(!props.item.showOnPortal) }"
              >
                {{ Boolean(props.item.showOnPortal) ? $t('global:yes') : $t('global:no') }}</span>
            </td>

            <td class="td">
              <span
                v-if="props.item.active === 1"
                class="td-tag active-ribbon"
              >{{ $t('global.status:active') }}</span>
              <span
                v-else
                class="td-tag inactive-ribbon"
              >{{ $t('global.status:inactive') }}</span>
            </td>
            <td class="td">
              <span class="td-text">{{ formatDate(props.item.createdAt) || '—' }}</span>
            </td>
            <td class="td">
              <span class="td-text td-block">{{ formatDate(props.item.userUpdatedAt || props.item.createdAt) || '—' }}</span>
              <span
                v-if="props.item.userUpdatedAt || props.item.createdAt"
                class="td-text td-audit-sub"
              >{{ formatTime(props.item.userUpdatedAt || props.item.createdAt) }}h{{ auditAuthorLabel(props.item) }}</span>
            </td>
            <td
              class="td td-actions"
              width="40"
            >
              <Dropdown
                icon="dots-vertical"
                right
              >
                <DropdownLink
                  v-if="canWrite"
                  :text="$t('global:edit')"
                  @click="edit(props.item.id)"
                />
                <DropdownLink
                  :text="$t('global:show.page')"
                  @click="openPage(props.item)"
                />
                <DropdownLink
                  :text="$t('tracks.list:action.links')"
                  @click="edit(props.item.id, 4)"
                />
                <DropdownLink
                  v-if="!props.item.active && canWrite"
                  :text="$t('global:activate')"
                  @click="toggleActive(props.item.id, props.item.active)"
                />
                <DropdownLink
                  v-else-if="canWrite"
                  :text="$t('global:deactivate')"
                  @click="toggleActive(props.item.id, props.item.active)"
                />
                <template v-if="isNonProdEnv && canWrite">
                  <div class="dropdown-devqa-separator">
                    <span class="dropdown-devqa-title">Ações rápidas (DEV/QA)</span>
                  </div>
                  <div
                    :class="['dropdown-toggle-wrapper', getToggleClass(Boolean(props.item.active))]"
                  >
                    <DropdownLink
                      :text="`Situação: ${Boolean(props.item.active) ? 'Ativo' : 'Inativo'}`"
                      @click="toggleActive(props.item.id, props.item.active)"
                    />
                  </div>
                  <div
                    v-if="typeof props.item.showOnPortal !== 'undefined'"
                    :class="['dropdown-toggle-wrapper', getToggleClass(Boolean(props.item.showOnPortal))]"
                  >
                    <DropdownLink
                      :text="`Exibir no portal: ${Boolean(props.item.showOnPortal) ? 'Sim' : 'Não'}`"
                      @click="toggleField(props.item.id, 'show_on_portal', props.item.showOnPortal)"
                    />
                  </div>
                  <div
                    v-if="typeof props.item.exclusiveToCompany !== 'undefined'"
                    :class="['dropdown-toggle-wrapper', getToggleClass(Boolean(props.item.exclusiveToCompany))]"
                  >
                    <DropdownLink
                      :text="`Exclusivo PJ: ${Boolean(props.item.exclusiveToCompany) ? 'Sim' : 'Não'}`"
                      @click="toggleField(props.item.id, 'exclusiveToCompany', props.item.exclusiveToCompany)"
                    />
                  </div>
                </template>
              </Dropdown>
            </td>
          </tr>
        </template>
      </Datatable>
      <Pagination
        :active-page="pagination.page"
        :page-count="pagination.lastPage"
        @firstPage="firstPage"
        @lastPage="lastPage"
        @nextPage="nextPage"
        @previousPage="prevPage"
      />
    </div>
    <div
      v-else
      class="center tracks-table"
    >
      <p>Nenhuma trilha encontrada para o termo pesquisado</p>
    </div>
  </div>
</template>

<style src="@/assets/styles/themes/default/tracks.css"></style>
